import { type FC, useMemo } from 'react';
import { graphql, useFragment, usePaginationFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { NftHeartPillProvider } from 'dibs-nft-heart-pill/exports/NftHeartPillProvider';
import { NftProfileSectionLoadMore } from '../NftProfileSectionLoadMore';
import { NftProfileImportCollectionViewNftTile } from './NftProfileImportCollectionViewNftTile';

import { type NftProfileImportCollectionViewNftList_contractImport$key } from './__generated__/NftProfileImportCollectionViewNftList_contractImport.graphql';
import { type NftProfileImportCollectionViewNftList_viewer$key } from './__generated__/NftProfileImportCollectionViewNftList_viewer.graphql';

import sectionsStyles from '../NftProfileSections.scss';
import styles from './styles/NftProfileImportCollectionViewNftList.scss';

const refetchFragment = graphql`
    fragment NftProfileImportCollectionViewNftList_contractImport on NftTokenContractImport
    @argumentDefinitions(
        first: { type: "Int", defaultValue: 20 }
        after: { type: "String" }
        importStatuses: {
            type: "[NftItemImportStatus!]"
            defaultValue: [NFT_ITEM_IMPORT_STATUS_COMPLETE, NFT_ITEM_IMPORT_STATUS_PROCESSING]
        }
    )
    @refetchable(queryName: "ListPaginationQuery") {
        items(first: $first, after: $after, importStatuses: $importStatuses)
            @connection(key: "NftProfileImportCollectionViewNftList_items") {
            totalResults
            edges {
                node {
                    serviceId
                    ...NftProfileImportCollectionViewNftTile_nftItem
                }
            }
        }
    }
`;

const viewerFragment = graphql`
    fragment NftProfileImportCollectionViewNftList_viewer on Viewer {
        ...NftProfileImportCollectionViewNftTile_viewer
        ...NftHeartPillProvider_viewer
    }
`;

export const NftProfileImportCollectionViewNftList: FC<{
    contractImport?: NftProfileImportCollectionViewNftList_contractImport$key | null | undefined;
    viewer: NftProfileImportCollectionViewNftList_viewer$key;
    refetchCollection?: () => void;
}> = ({ contractImport: contractImportProp = null, viewer: viewerProp, refetchCollection }) => {
    const viewer = useFragment(viewerFragment, viewerProp);
    const { data, ...loadMoreProps } = usePaginationFragment(refetchFragment, contractImportProp);
    const [ids, items] = useMemo(() => {
        const _ids = [];
        const _items = [];
        for (const edge of data?.items?.edges || []) {
            if (edge?.node && edge.node.serviceId) {
                _items.push(edge.node);
                _ids.push(edge.node.serviceId);
            }
        }
        return [_ids, _items];
    }, [data]);

    return (
        <NftHeartPillProvider viewer={viewer} nftItemIds={ids}>
            <ul className={sectionsStyles.manifoldCollection}>
                {items.map(nftItem => (
                    <NftProfileImportCollectionViewNftTile
                        key={nftItem.serviceId}
                        viewer={viewer}
                        nftItem={nftItem}
                        refetchCollection={refetchCollection}
                    />
                ))}
            </ul>
            <p className={styles.emptyContract}>
                <FormattedMessage
                    id="nftProfile.importCollectionViewList.emptyContract"
                    defaultMessage="<strong>This collection is empty</strong>This collection does not have any NFTs yet. Once NFTs are added to this collection, they’ll be shown here."
                    values={{
                        strong: msg => <strong>{msg}</strong>,
                    }}
                />
            </p>
            <NftProfileSectionLoadMore {...loadMoreProps} count={20} />
        </NftHeartPillProvider>
    );
};
